/* ==========================================================================
   CAIOS theme — Graphite (dark) + Paper (light)         v2.1 · 2026-10-31
   Adam chose Graphite + Paper on 2026-10-31 (replacing Tide + Arctic). The attribute keys stay
   "tide" / "arctic" so saved preferences carry over; only the values and the visible names changed.
   Source of truth for every CAIOS 2.0 screen. Values mirror tokens.json.
   Decision: CAIOS 2.0 plan D8. Reference canvas:
   https://claude.ai/artifact/897BSH9QFAiSdnUDWnpzUU

   Usage
     <html data-theme="tide">   or   data-theme="arctic"
     No attribute → follows the OS (prefers-color-scheme).
     Org/partner customisation overrides ONLY the variables below
     (e.g. --cx-accent, --cx-font-sans) — never component rules.
   ========================================================================== */

/* ---------- Themes ---------- */
:root, [data-theme="tide"] {
  color-scheme: dark;
  --cx-ground: #0F1012;  --cx-ground-raised: #0F1012;  --cx-rail: #0B0C0E;
  --cx-panel: #17191C;   --cx-panel-deep: #131417;     --cx-inset: rgba(0,0,0,.35);
  --cx-line: rgba(255,255,255,.08);  --cx-line-strong: rgba(255,255,255,.16);  --cx-dot: transparent;
  --cx-text: #EDEEF0;    --cx-text-muted: #A6AAB2;    --cx-text-faint: #868B93;
  --cx-accent: #43E6D0;  --cx-accent-text: #43E6D0;  --cx-accent-alt: #6AA8FF;  --cx-on-accent: #04201B;
  --cx-eyebrow: #43E6D0; --cx-section-label: #E0A458; --cx-link: #7FD9EE;     --cx-control: #111214;
  --cx-fire: #F26464;    --cx-decision: #6AA8FF;     --cx-good: #3DD68C;      --cx-warn: #F5B544;  --cx-hot: #F26464;
  --cx-data-1: #3DD68C;  --cx-data-2: #6AA8FF;  --cx-data-3: #9AA6FF;  --cx-data-4: #43E6D0;  --cx-data-5: #F5B544;  --cx-data-6: #43E6D0;
  --cx-shadow-a: .5;
}
[data-theme="arctic"] {
  color-scheme: light;
  --cx-ground: #F6F5F2;  --cx-ground-raised: #F6F5F2;  --cx-rail: #EEECE7;
  --cx-panel: #FFFFFF;   --cx-panel-deep: #F8F7F4;     --cx-inset: rgba(240,238,233,.8);
  --cx-line: rgba(30,25,15,.10);  --cx-line-strong: rgba(30,25,15,.20);  --cx-dot: transparent;
  --cx-text: #17150F;    --cx-text-muted: #57534A;    --cx-text-faint: #6B675E;
  --cx-accent: #0B827E;  --cx-accent-text: #0A7672;  --cx-accent-alt: #2060EB;  --cx-on-accent: #FFFFFF;
  --cx-eyebrow: #0B7571; --cx-section-label: #A4520F; --cx-link: #0E6E73;     --cx-control: #FFFFFF;
  --cx-fire: #C62828;    --cx-decision: #2060EB;     --cx-good: #047857;      --cx-warn: #AD5009;  --cx-hot: #C62828;
  --cx-data-1: #047857;  --cx-data-2: #2060EB;  --cx-data-3: #4F46E5;  --cx-data-4: #0E7490;  --cx-data-5: #AD5009;  --cx-data-6: #0A7672;
  --cx-shadow-a: .08;
}
@media (prefers-color-scheme: light) {
  :root:not([data-theme]) {
    color-scheme: light;
    --cx-ground: #F6F5F2;  --cx-ground-raised: #F6F5F2;  --cx-rail: #EEECE7;
    --cx-panel: #FFFFFF;   --cx-panel-deep: #F8F7F4;     --cx-inset: rgba(240,238,233,.8);
    --cx-line: rgba(30,25,15,.10);  --cx-line-strong: rgba(30,25,15,.20);  --cx-dot: transparent;
    --cx-text: #17150F;    --cx-text-muted: #57534A;    --cx-text-faint: #6B675E;
    --cx-accent: #0B827E;  --cx-accent-text: #0A7672;  --cx-accent-alt: #2060EB;  --cx-on-accent: #FFFFFF;
    --cx-eyebrow: #0B7571; --cx-section-label: #A4520F; --cx-link: #0E6E73;     --cx-control: #FFFFFF;
    --cx-fire: #C62828;    --cx-decision: #2060EB;     --cx-good: #047857;      --cx-warn: #AD5009;  --cx-hot: #C62828;
    --cx-data-1: #047857;  --cx-data-2: #2060EB;  --cx-data-3: #4F46E5;  --cx-data-4: #0E7490;  --cx-data-5: #AD5009;  --cx-data-6: #0A7672;
    --cx-shadow-a: .08;
  }
}

/* ---------- Foundations ---------- */
:root {
  --cx-font-sans: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  --cx-font-mono: "SF Mono", "JetBrains Mono", ui-monospace, Menlo, monospace;
  --cx-r-hero: 18px; --cx-r-panel: 16px; --cx-r-card: 14px; --cx-r-row: 10px; --cx-r-control: 9px; --cx-r-chip: 7px;
  --cx-ease-enter: cubic-bezier(.2,.8,.2,1); --cx-ease-exit: cubic-bezier(.5,0,.75,0); --cx-ease-draw: cubic-bezier(.3,.7,.2,1);
  --cx-rail-w: 72px;
  /* One spacing rhythm (2026-10-29 calm pass): every gap, padding and margin is one of these. */
  --cx-s1: 4px; --cx-s2: 8px; --cx-s3: 12px; --cx-s4: 16px; --cx-s5: 20px; --cx-s6: 24px; --cx-s8: 32px;
}
.cx-app {
  font-family: var(--cx-font-sans); color: var(--cx-text); -webkit-font-smoothing: antialiased;
  background: var(--cx-ground);
}
.cx-mono { font-family: var(--cx-font-mono); }
.cx-num  { font-variant-numeric: tabular-nums; }
.cx-app a { color: var(--cx-link); text-decoration: underline; text-decoration-color: color-mix(in srgb, var(--cx-link) 45%, transparent); text-underline-offset: 3px; }

/* ---------- Type ----------
   Calm pass (2026-10-29): small labels are sentence case in the UI face, not tracked mono caps; nothing glows. */
.cx-eyebrow { font: 500 13px/1.4 var(--cx-font-sans); color: var(--cx-text-faint); }
.cx-eyebrow > b { font-weight: 600; color: var(--cx-eyebrow); }
.cx-label   { font: 500 12px/1.3 var(--cx-font-sans); color: var(--cx-text-faint); }
.cx-rail-label { font: 400 9px/1 var(--cx-font-mono); letter-spacing: .14em; text-transform: uppercase; color: var(--cx-section-label); }
.cx-display { font-size: 28px; font-weight: 600; letter-spacing: -.02em; text-wrap: balance; }
h1, h2, h3 { text-wrap: balance; }
.cx-hero    { font-size: 28px; font-weight: 600; letter-spacing: -.02em; }
.cx-hero em, .cx-display em { font-style: normal; color: var(--cx-text-faint); }
.cx-h2      { font-size: 20px; font-weight: 600; letter-spacing: -.01em; }
.cx-body    { font-size: 13.5px; line-height: 1.55; color: var(--cx-text-muted); }

/* ---------- Surfaces ---------- */
.cx-rail { width: var(--cx-rail-w); background: var(--cx-rail); border-right: 1px solid var(--cx-line); }
.cx-panel { background: var(--cx-panel); border: 1px solid var(--cx-line); border-radius: var(--cx-r-panel); }
.cx-inset { background: var(--cx-inset); border: 1px solid var(--cx-line); border-radius: 12px; }
.cx-hero-panel {
  position: relative; overflow: hidden; border-radius: var(--cx-r-hero); border: 1px solid var(--cx-line); background: var(--cx-panel);
  box-shadow: 0 1px 0 color-mix(in srgb, #fff 5%, transparent) inset, 0 12px 32px -18px rgba(0,0,0,var(--cx-shadow-a));
}

/* ---------- Data ---------- */
.cx-stat { position: relative; overflow: hidden; padding: var(--cx-s4) var(--cx-s4) var(--cx-s3); border-radius: var(--cx-r-card);
  background: var(--cx-panel); border: 1px solid var(--cx-line); --c: var(--cx-data-1); }
/* The tile's colour is a status dot by its label; the number stays in the text colour. */
.cx-stat > .cx-label { display: flex; align-items: center; gap: var(--cx-s2); }
.cx-stat > .cx-label::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: var(--c); flex: none; }
.cx-stat .cx-value { margin-top: var(--cx-s2); font-size: 28px; font-weight: 500; letter-spacing: -.02em; color: var(--cx-text); font-variant-numeric: tabular-nums; }
.cx-pill { display: inline-flex; align-items: center; gap: 7px; padding: 5px 10px; border-radius: 8px;
  font: 500 12px/1 var(--cx-font-sans);
  color: var(--c, var(--cx-text-muted)); background: color-mix(in srgb, var(--c, var(--cx-text-muted)) 16%, transparent); }
.cx-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; animation: cx-breathe 2s ease-in-out infinite; }
.cx-pill--fire { --c: var(--cx-fire); }  .cx-pill--decision { --c: var(--cx-decision); }  .cx-pill--good { --c: var(--cx-good); }
.cx-age { font: 400 11.5px/1 var(--cx-font-mono); padding: 4px 8px; border-radius: var(--cx-r-chip);
  color: var(--cx-warn); background: color-mix(in srgb, var(--cx-warn) 16%, transparent); }
.cx-age--late { color: var(--cx-hot); background: color-mix(in srgb, var(--cx-hot) 16%, transparent); } /* > 30 days */

/* ---------- Controls ---------- */
.cx-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; box-sizing: border-box; text-decoration: none;
  font: 600 13px/1 var(--cx-font-sans); min-height: 36px; padding: 8px 14px; border-radius: var(--cx-r-control); cursor: pointer;
  color: var(--cx-text); background: var(--cx-control); border: 1px solid var(--cx-line-strong);
  transition: transform .12s ease, border-color .12s ease, box-shadow .12s ease, opacity .2s ease; }
.cx-btn:hover { border-color: var(--cx-accent); }
.cx-btn:active { transform: scale(.98); }
.cx-btn:focus-visible { outline: 2px solid var(--cx-accent); outline-offset: 2px; }
.cx-btn--primary { background: var(--cx-accent); border-color: var(--cx-accent); color: var(--cx-on-accent); }
.cx-btn--primary[aria-disabled="true"] { opacity: .38; cursor: not-allowed; transform: none; box-shadow: none; }
.cx-row { display: flex; align-items: center; gap: 14px; padding: 10px 12px; border-radius: var(--cx-r-row); transition: background .15s ease, transform .15s ease; }
.cx-row:hover { background: color-mix(in srgb, var(--cx-accent) 6%, transparent); transform: translateX(3px); }
.cx-row[aria-selected="true"] { background: color-mix(in srgb, var(--cx-accent) 13%, transparent); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--cx-accent) 35%, transparent); }
.cx-toast { position: fixed; left: 50%; bottom: 28px; transform: translateX(-50%); padding: 10px 16px; border-radius: 10px;
  font: 500 13px/1.3 var(--cx-font-sans); color: var(--cx-text); background: var(--cx-panel); border: 1px solid var(--cx-line-strong);
  box-shadow: 0 16px 40px rgba(0,0,0,var(--cx-shadow-a)); animation: cx-toast 2.6s var(--cx-ease-enter) forwards; }

/* ---------- Motion ---------- */
@keyframes cx-rise    { from { opacity: 0; transform: translateY(14px); filter: blur(5px); } to { opacity: 1; transform: none; filter: none; } }
@keyframes cx-rail-in { from { opacity: 0; transform: translateX(-10px) scale(.9); } to { opacity: 1; transform: none; } }
@keyframes cx-write   { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0 0 0); } }
@keyframes cx-caret   { 0%,100% { opacity: 1; } 50% { opacity: 0; } }
@keyframes cx-draw    { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
@keyframes cx-fade    { from { opacity: 0; } to { opacity: 1; } }
@keyframes cx-leave   { to { opacity: 0; transform: translateX(48px); max-height: 0; padding-top: 0; padding-bottom: 0; margin: 0; border-width: 0; } }
@keyframes cx-fold    { to { opacity: 0; transform: translateY(-6px) scale(.98); max-height: 0; padding-top: 0; padding-bottom: 0; margin-top: 0; border-width: 0; } }
@keyframes cx-unlock  { 0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--cx-accent) 60%, transparent); } 100% { box-shadow: 0 0 0 10px transparent; } }
@keyframes cx-pulse   { 0%,100% { box-shadow: 0 0 0 0 transparent; } 50% { box-shadow: 0 0 0 7px color-mix(in srgb, var(--cx-accent-alt) 22%, transparent); } }
@keyframes cx-breathe { 0%,100% { opacity: .75; } 50% { opacity: 1; } }
@keyframes cx-sheen   { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
@keyframes cx-tick    { from { transform: translateY(-3px); opacity: .4; } to { transform: none; opacity: 1; } }
@keyframes cx-toast   { 0% { opacity: 0; transform: translate(-50%, 16px); } 12% { opacity: 1; transform: translate(-50%, 0); } 85% { opacity: 1; } 100% { opacity: 0; transform: translate(-50%, -6px); } }

/* Set --d (delay) per element: style="--d: 120ms". Stagger: rail 40 · list 70 · tiles 80 · stats 90 · agent lines 300 (ms). */
.cx-rise    { animation: cx-rise .55s var(--cx-ease-enter) both; animation-delay: var(--d, 0ms); }
.cx-rail-in { animation: cx-rail-in .45s var(--cx-ease-enter) both; animation-delay: var(--d, 0ms); }
.cx-write   { animation: cx-write .55s steps(28, end) both; animation-delay: var(--d, 0ms); }
.cx-caret   { display: inline-block; width: 7px; height: 15px; margin-left: 3px; vertical-align: -2px; background: var(--cx-accent); animation: cx-caret 1s step-end infinite; }
.cx-draw    { stroke-dasharray: 1; animation: cx-draw 1.2s var(--cx-ease-draw) both; animation-delay: var(--d, 0ms); } /* needs pathLength="1" on the path */
.cx-area    { animation: cx-fade .8s ease both; animation-delay: var(--d, 0ms); }
.cx-leave   { animation: cx-leave .4s var(--cx-ease-exit) forwards; overflow: hidden; }
.cx-fold    { animation: cx-fold .45s var(--cx-ease-exit) forwards; overflow: hidden; }
.cx-unlock  { animation: cx-unlock .7s ease-out; }
.cx-attention { animation: cx-pulse 2.4s ease-in-out infinite; }   /* one per screen, max */
.cx-sheen   { position: relative; overflow: hidden; }
.cx-sheen::after { content: ""; position: absolute; inset: 0; pointer-events: none; transform: translateX(-100%);
  background: linear-gradient(100deg, transparent 30%, color-mix(in srgb, #fff 7%, transparent) 50%, transparent 70%);
  animation: cx-sheen 1.4s ease both; animation-delay: var(--d, 0ms); }
.cx-tick    { animation: cx-tick .35s ease-out; }

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation: none !important; transition: none !important; }
}

/* A partner portal wears its own accent (apps/web brand.ts sets data-brand and the two --cx-brand-* values on <html>).
   On the light theme the accent is darkened for text, so a pale brand colour (Live Echo's gold) stays readable. */
:root[data-brand] { --cx-accent: var(--cx-brand-accent); --cx-on-accent: var(--cx-brand-on-accent); --cx-accent-text: var(--cx-brand-accent); --cx-eyebrow: var(--cx-brand-accent); }
:root[data-brand][data-theme="arctic"] { --cx-accent-text: color-mix(in srgb, var(--cx-brand-accent) 55%, #000); --cx-eyebrow: color-mix(in srgb, var(--cx-brand-accent) 55%, #000); }
